import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import DeleteConfirmationModal from './projects/DeleteConfirmationModal';

interface PendingPost {
  _id: string;
  fileName: string;
  content: string;
  createdTime: string;
  modifiedTime: string;
  userId: string;
  documentId: string;
  revisionId: string;
}

const PendingPostPage: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [summaries, setSummaries] = useState<PendingPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PendingPost | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null); // State to manage the id of the post to be highlighted
  const [highlightDismissed, setHighlightDismissed] = useState(false); // Track if highlight was dismissed by save
  const editAreaRef = useRef<HTMLDivElement | null>(null);
  const prevLength = useRef<number>(0); // Ref to store the previous length of summaries

  useEffect(() => {
    const fetchSummaries = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/pending-posts?userId=${user?.id}`);
        const data = await res.json();
        if (data.success) {
          setSummaries(data.data);
        } else {
          setError(data.message || 'Failed to fetch summaries');
        }
      } catch (err) {
        setError('Failed to fetch summaries');
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) fetchSummaries();
  }, [user]);

  // When summaries are loaded, apply highlight if revisionId param is present and not dismissed
  useEffect(() => {
    if (highlightDismissed) return;
    const params = new URLSearchParams(window.location.search);
    const pendingPostId = params.get('pendingPostId');
    if (pendingPostId && summaries.length > 0) {
      const target = summaries.find((p) => p._id === pendingPostId);
      if (target) {
        setHighlightId(target._id);
      } else {
        setHighlightId(null);
      }
    } else {
      setHighlightId(null);
    }
  }, [summaries, highlightDismissed]);

  const handleUpdate = (post: PendingPost) => {
    // TODO: Implement update & save logic (e.g., open modal for editing, then save)
    alert(`Update & Save for post: ${post.fileName}`);
  };

  const handleDelete = async (postId: string) => {
    setShowDeleteModal(false);
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/pending-posts/delete/${postId}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.success) {
        setSummaries((prev) => prev.filter((p) => p._id !== postId));
        addToast('Post deleted successfully.', 'success');
      } else {
        addToast(data.message || 'Failed to delete post', 'error');
      }
    } catch (err) {
      addToast('Failed to delete post', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async (post: PendingPost) => {
    setLoading(true);
    try {
      // 1. Delete the pending post
      const deleteRes = await fetch(`${process.env.REACT_APP_API_URL}/api/pending-posts/delete/${post._id}`, {
        method: 'DELETE',
      });
      const deleteData = await deleteRes.json();
      if (!deleteData.success) {
        addToast(deleteData.message || 'Failed to delete pending post', 'error');
        setLoading(false);
        return;
      }
      // 2. Create a new post with title + content, with Authorization header if session_token exists
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      const createRes = await fetch(`${process.env.REACT_APP_API_URL}/api/posts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          title: post.fileName,
          content: post.content,
        }),
      });
      const createData = await createRes.json();
      if (createRes.ok) {
        setSummaries((prev) => prev.filter((p) => p._id !== post._id));
        addToast('Post published successfully!', 'success');
        window.location.href = '/feed';
        return;
      } else {
        addToast(createData.message || 'Failed to create post', 'error');
        setLoading(false);
        return;
      }
    } catch (err) {
      addToast('Failed to publish post', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (post: PendingPost) => {
    setEditingId(post._id);
    setEditContent(post.content);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditContent('');
  };

  const handleEditSave = async (post: PendingPost) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/pending-posts/update/${post._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pendingPostId: post._id, // pendinPost Object ID
          content: editContent
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSummaries((prev) => prev.map((p) =>
          p._id === post._id
            ? { ...p, content: editContent, modifiedTime: data.data.modifiedTime }
            : p
        ));
        setEditingId(null);
        setEditContent('');
        addToast('Saved successfully.', 'success');
        if (post._id === highlightId) {
          setHighlightId(null);
          setHighlightDismissed(true);
        }
      } else {
        setError(data.message || 'Failed to update post');
        addToast(data.message || 'Failed to update post', 'error');
      }
    } catch (err) {
      setError('Failed to update post');
      addToast('Failed to update post', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!editingId) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (editAreaRef.current && !editAreaRef.current.contains(event.target as Node)) {
        if (editingId) {
          const editingPost = summaries.find((p) => p._id === editingId);
          if (editingPost) handleEditSave(editingPost);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editingId, editContent, summaries]);

  const latestId = summaries.length > 0 ? summaries.reduce((latest, curr) => {
    return new Date(curr.createdTime) > new Date(latest.createdTime) ? curr : latest;
  }, summaries[0])._id : null;

  return (
    <div className="max-w-3xl mx-auto py-8">
      <h1 className="text-2xl font-bold mb-6 text-white">Document Summary Posts</h1>
      {loading && <div>Loading...</div>}
      {error && <div className="text-red-600">{error}</div>}
      <div className="space-y-6">
        {summaries.map((post) => (
          <div key={post._id} className={`bg-paper border-2 border-parchment-400 rounded-lg shadow p-4 ${(post._id === highlightId && !highlightDismissed) ? 'border-yellow-400 animate-pulse' : ''}`}>
            <div className="flex justify-between items-center mb-2 bg-parchment-50 rounded px-3 py-2 border-2 border-parchment-300">
              <div className="font-semibold text-custom-blue-800">{post.fileName}</div>
              <div className="text-xs text-custom-blue-500">
                {post.modifiedTime
                  ? `Last edited: ${new Date(post.modifiedTime).toLocaleString()}`
                  : `Created: ${new Date(post.createdTime).toLocaleString()}`}
              </div>
            </div>
            <div 
              ref={editingId === post._id ? editAreaRef : undefined}
              className={`bg-parchment-50 rounded px-3 py-2 text-custom-blue-900 whitespace-pre-line mb-2 cursor-pointer border-2 border-parchment-200 ${editingId === post._id ? 'min-h-[180px]' : ''}`}
              onClick={() => {
                if (editingId !== post._id) handleEditClick(post);
                if (post._id === highlightId) {
                  setHighlightId(null);
                  setHighlightDismissed(true);
                }
              }}
            >
              {editingId === post._id ? (
                <>
                  <textarea
                    className="w-full border border-parchment-300 rounded p-2 mb-2 bg-paper text-custom-blue-900 min-h-[120px]"
                    rows={8}
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    autoFocus
                  />
                  <div className="flex gap-2 justify-end mb-2">
                    <button
                      className="px-3 py-1 bg-custom-blue-600 text-white rounded hover:bg-custom-blue-700 text-sm font-medium"
                      onClick={e => { e.stopPropagation(); handleEditSave(post); }}
                      disabled={loading}
                    >
                      Save
                    </button>
                    <button
                      className="px-3 py-1 bg-gray-300 text-custom-blue-900 rounded hover:bg-gray-400 text-sm font-medium"
                      onClick={e => { e.stopPropagation(); handleEditCancel(); }}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {post.content}
                </>
              )}
            </div>
            <div className="flex justify-center gap-4 mt-4">
              {editingId !== post._id && (
                <>
                  <button
                    className="min-w-[110px] px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm font-medium"
                    onClick={() => handlePost(post)}
                  >
                    Post
                  </button>
                  <button
                    className="min-w-[110px] px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm font-medium"
                    onClick={() => {
                      setDeleteTarget(post);
                      setShowDeleteModal(true);
                    }}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      {showDeleteModal && deleteTarget && (
        <DeleteConfirmationModal
          show={showDeleteModal}
          projectName={deleteTarget.fileName}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={() => handleDelete(deleteTarget._id)}
          titleText="Delete Pending Post"
          descriptionText="Are you sure you want to delete this pending post?"
        />
      )}
    </div>
  );
};

export default PendingPostPage;
